import React, { FunctionComponent } from 'react';
import { Card, Snackbar, makeStyles } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import highlightJs from 'highlight.js';
import useCommonCodeBlockStyles from '@modules/miscellaneous/common/styles/codeBlock.styles';
import { useCodeStyles } from './CodeStylesProvider';
import useCodeBlockState from './hooks/useCodeBlockState';
import useCodeBlockContent from './hooks/useCodeBlockContent';
import CodeBlockHeader from './components/CodeBlockHeader';
import CodeBlockCore from './components/CodeBlockCore';
import luau from './highlightjs-luau/index';

highlightJs.registerLanguage('luau', luau);

export type TCodeSampleProps = {
  fullWidth?: boolean;
  width?: number;
  maxContentHeight?: number;
};

export type TCodeBlockProps = TCodeSampleProps & {
  title?: string;
  language?: string;
  content: string;
  className?: string;
  enableCopy?: boolean;
  enableExpand?: boolean;
  assetId?: number;
};

const useCodeBlockStyles = makeStyles<TCodeSampleProps>()((theme, props) => ({
  card: {
    width: props.fullWidth ? '100%' : (props.width ?? 500),
    backgroundColor: 'transparent',
  },
}));

const CodeBlock: FunctionComponent<TCodeBlockProps> = (props) => {
  const {
    title,
    content,
    assetId,
    language,
    maxContentHeight = 400,
    enableCopy,
    enableExpand,
    className,
    width,
    fullWidth,
  } = props;

  // Styles
  const {
    classes: { codeBlock },
    cx,
  } = useCommonCodeBlockStyles();
  const { hideHeaderInSingleLine, fullHeight } = useCodeStyles();
  const { classes } = useCodeBlockStyles({ fullWidth, width, maxContentHeight });

  // State management
  const {
    isContentExpanded,
    showSnackbar,
    handleExpandContent,
    handleCopyToClipboard,
    setShowSnackbar,
  } = useCodeBlockState();

  // Content analysis
  const { isMultiline, exceedsMaxHeight } = useCodeBlockContent(content, maxContentHeight);

  const { translate } = useTranslation();
  const snackbarText = translate('Label.CodeSnippetCopyMessage') || 'Copied to clipboard';

  const showHeader =
    (title || enableCopy || enableExpand) && !(hideHeaderInSingleLine && !isMultiline);

  return (
    <React.Fragment>
      <Card className={cx(classes.card, codeBlock, className)}>
        {showHeader && (
          <CodeBlockHeader
            title={title}
            assetId={assetId}
            enableCopy={enableCopy}
            enableExpand={enableExpand}
            isContentExpanded={isContentExpanded}
            exceedsMaxHeight={exceedsMaxHeight}
            maxContentHeight={maxContentHeight}
            onCopy={() => handleCopyToClipboard(content)}
            onExpand={handleExpandContent}
          />
        )}
        <CodeBlockCore
          content={content}
          language={language}
          isExpanded={isContentExpanded}
          maxContentHeight={maxContentHeight}
          fullHeight={fullHeight}
        />
      </Card>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        message={snackbarText}
        autoHideDuration={3000}
      />
    </React.Fragment>
  );
};
export default CodeBlock;
